import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../contexts/StoreContext';
import GestureArea from '../../gesture-area/GestureArea';
import {
  GestureStateChangeEvent,
  GestureUpdateEvent,
  PanGestureHandlerEventPayload,
  PinchGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import { UserImageWithTransform as UserImage } from '../../user-image/UserImageWithTransform';
import { UserImageAreaModelInstance } from '../../../store/_models/userImageArea';
import { IUserImageModel } from '../../../store/_models/userImage';
import { calculateZoomValue } from '../../../helpers/zoom.helper';
import { UserImageArea } from '@photosi/rn-components/user-image-area/UserImageArea';
import { UserImageAreaGridOverlay } from '@photosi/rn-components/user-image-area/user-image-area-grid-overlay/UserImageAreaGridOverlay';
import { useWheelZoom } from '../../../hooks/useWheelZoom';
import { emit } from '../../../contexts/TransformEventBus';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useResponsiveScale } from 'src/contexts/ResponsiveScaleContext';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { SliderZoom } from '@photosi/rn-components/tools/slider-zoom/SliderZoom';
import { getFormatI18nKey } from 'src/_shared/utils/getI18nKeys';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';

export type UserImagesFullscreenProps = {
  userImageArea: UserImageAreaModelInstance;
  editable: boolean;
  showGrid: boolean;
  disabled?: boolean;
  disableSwapImageAnimation?: boolean;
  focusOrderBase?: number;
  focusIdPrefix?: string;
  onPanStart?: () => void;
  onPanEnd?: () => void;
};

export type CropPreviewContentHandle = {
  // Nudges the image by a pixel delta, mirroring what a mouse-drag gesture applies on release.
  nudge: (dx: number, dy: number) => void;
};

/**
 * Represents the collage of all the images to be inserted into the
 * photo space (userImageAreas) in the product (userImagesForPreviewSvg).
 */
const CropPreviewContentComponent = observer(
  forwardRef<CropPreviewContentHandle, UserImagesFullscreenProps>(
    (
      {
        userImageArea,
        editable = false,
        showGrid = false,
        disabled = false,
        disableSwapImageAnimation = false,
        focusOrderBase = 0,
        focusIdPrefix,
        onPanStart,
        onPanEnd,
      }: UserImagesFullscreenProps,
      ref,
    ) => {
      // console.log("FullscreenPreviewContent rerender");
      const { i18n } = useStore();

      const { isDesktop } = useResponsiveScreen();
      const lastPinchZoomRef = useRef<number | undefined>();
      const [hoveringAreaId, setHoveringAreaId] = useState<string | null>(null);

      // Zoom with wheel logic
      const isHoveringOnActiveUserImageArea = !!hoveringAreaId && hoveringAreaId === userImageArea?.id;
      const isZoomWithWheelActive = isHoveringOnActiveUserImageArea;
      useWheelZoom({ isActive: isZoomWithWheelActive, userImage: userImageArea?.userImage });

      /**
       * GESTURE HANDLERS
       */

      // Calculate the new image scale using a dynamic speed approach
      const calculateScaleFrom = (userImage: IUserImageModel, eventScale: number) => {
        if (lastPinchZoomRef.current === undefined) lastPinchZoomRef.current = eventScale;
        const zoomFactor = eventScale / lastPinchZoomRef.current;
        lastPinchZoomRef.current = eventScale;
        return calculateZoomValue(userImage.scale * zoomFactor, userImage.minScale, userImage.maxScale);
      };

      const pinchHandler = (event: GestureUpdateEvent<PinchGestureHandlerEventPayload>, userImage: IUserImageModel) => {
        const newScale = calculateScaleFrom(userImage, event.scale);

        // userImage.setScale(newScale, responsiveScale);
        emit({
          type: 'temp-zoom',
          userImageId: userImage.id,
          payload: { absoluteScale: newScale },
          source: 'gesture',
        });
      };

      const pinchEndHandler = (
        event: GestureStateChangeEvent<PinchGestureHandlerEventPayload>,
        userImage: IUserImageModel,
      ) => {
        const newScale = calculateScaleFrom(userImage, event.scale);

        // userImage.applyScale(newScale, responsiveScale);
        emit({
          type: 'apply-zoom',
          userImageId: userImage.id,
          payload: { absoluteScale: newScale },
          source: 'gesture',
        });

        lastPinchZoomRef.current = undefined;
      };

      /**
       * DRAG IMAGE HANDLERS
       */
      const dragImageChange = (
        event: GestureUpdateEvent<PanGestureHandlerEventPayload>,
        userImage: IUserImageModel,
      ) => {
        // userImage.setOffsets({ x: event.translationX, y: event.translationY });

        // NUOVO: Emit evento temporaneo direttamente da gesture (UI thread)
        emit({
          type: 'temp-pan',
          userImageId: userImage.id,
          payload: { x: event.translationX, y: event.translationY },
          source: 'gesture',
        });
      };

      const dragImageEnd = (
        event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
        userImage: IUserImageModel,
      ) => {
        // userImage.applyOffsets({ x: event.translationX, y: event.translationY }, responsiveScale);

        // NUOVO: Emit evento apply direttamente da gesture (UI thread)
        emit({
          type: 'apply-pan',
          userImageId: userImage.id,
          payload: { x: event.translationX, y: event.translationY },
          source: 'gesture',
        });

        onPanEnd?.();
      };

      useImperativeHandle(
        ref,
        () => ({
          nudge: (dx: number, dy: number) => {
            emit({
              type: 'apply-pan',
              userImageId: userImageArea.userImage!.id,
              payload: { x: dx, y: dy },
              source: 'keyboard',
            });
          },
        }),
        [userImageArea.userImage],
      );

      const isSelected: boolean = true;
      const showGridOverlay: boolean = isSelected && editable && showGrid;

      const { responsiveScaleSV } = useResponsiveScale();

      const sizeAnimatedStyle = useAnimatedStyle(() => ({
        width: userImageArea.width * responsiveScaleSV.value,
        height: userImageArea.height * responsiveScaleSV.value,
        alignItems: 'center',
        justifyContent: 'center',
      }));

      return (
        <Animated.View style={sizeAnimatedStyle}>
          <UserImageArea userImageArea={userImageArea} isSelected={isSelected} ignoreOffsets>
            <GestureArea
              disabled={disabled}
              onPanStart={() => onPanStart?.()}
              onPan={event => dragImageChange(event, userImageArea.userImage!)}
              onPanEnd={event => dragImageEnd(event, userImageArea.userImage!)}
              onPinch={event => pinchHandler(event, userImageArea.userImage!)}
              onPinchEnd={event => pinchEndHandler(event, userImageArea.userImage!)}
              onHoverStart={() => setHoveringAreaId(userImageArea.id)}
              onHoverEnd={() => setHoveringAreaId(prev => (prev === userImageArea.id ? null : prev))}
            >
              <TextLato style={style.text} numberOfLines={1}>
                {i18n.t(getFormatI18nKey(userImageArea?.product?.family, userImageArea?.product?.choices?.format))}
              </TextLato>
              {showGridOverlay && <UserImageAreaGridOverlay extBorderColor="#FAA100" />}
              <UserImage userImage={userImageArea.userImage!} showOverflow editable disableSwapImageAnimation={true} />
              <UserImage
                userImage={userImageArea.userImage!}
                editable={false}
                disableSwapImageAnimation={disableSwapImageAnimation}
              />
              {isDesktop && (
                <View style={style.zoom}>
                  <SliderZoom
                    customUserImage={userImageArea.userImage}
                    focusGroup={FocusGroups.MODAL}
                    focusOrderBase={focusOrderBase}
                    focusIdPrefix={focusIdPrefix}
                  />
                </View>
              )}
            </GestureArea>
          </UserImageArea>
        </Animated.View>
      );
    },
  ),
);

const style = StyleSheet.create({
  text: {
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 16,
    textAlign: 'left',
    width: '100%',
    position: 'absolute',
    zIndex: 10,
    top: -24,
  },
  zoom: {
    position: 'absolute',
    bottom: -48,
    zIndex: 10,
  },
});

export const CropPreviewContent = CropPreviewContentComponent;
